import { graphqlSchema } from './graphql.schema';
import { getCookies } from './document.cookie';

import { split, HttpLink, ApolloClient, InMemoryCache, NormalizedCacheObject, setLogVerbosity } from '@apollo/client';
import { getMainDefinition } from '@apollo/client/utilities';
import { GraphQLWsLink } from '@apollo/client/link/subscriptions';
import { createClient, Client } from 'graphql-ws';

import { FiscalpopProfile } from '../models/Profile';

import LogRocket from 'logrocket';

const LEGACY_COOKIE = 'mws_profileid';
const FP_SESSION_COOKIE = 'fp_mws_profileid';

const WS_URL = process.env.REACT_APP_WS_URL;
const HTTP_URL = process.env.REACT_APP_HTTP_URL;
const HOME_REDIRECT_URL = process.env.REACT_APP_HOME_REDIRECT_URL;
const LOGOUT_URL = `${new URL(HTTP_URL).origin}/logout`;

setLogVerbosity('error');

class GraphQlClientService {
    private _client: ApolloClient<NormalizedCacheObject>;
    private _wsClient: Client;

    constructor() {
        // Server reads fp_mws_profileid from the WS upgrade Cookie header; no connectionParams needed.
        this._wsClient = createClient({ url: WS_URL });
        const wsLink = new GraphQLWsLink(this._wsClient);

        const httpLink = new HttpLink({
            uri: HTTP_URL,
            fetch: window.fetch,
            credentials: 'include',
        });

        const splitLink = split(
            ({ query }) => {
                const definition = getMainDefinition(query);
                return (
                    definition.kind === 'OperationDefinition' &&
                    definition.operation === 'subscription'
                );
            },
            wsLink,
            httpLink,
        );

        this._client = new ApolloClient({
            link: splitLink,
            cache: new InMemoryCache(),
        });

        // Tab focus session checker — alerts on cross-tab account swap detected via fp_mws_profileid rotation.
        const forOnFocus = (() => {
            let prev = getCookies()[FP_SESSION_COOKIE] || '';
            return () => {
                const next = getCookies()[FP_SESSION_COOKIE] || '';
                if (!prev || !next) { prev = next; return; }
                if (prev !== next) {
                    prev = next;
                    console.log('<WinFocus> session change detected');
                    LogRocket.track('SessionMismatchError');
                    alert(`Se detectó una sessión de FiscalPOP diferente, se recargarán los datos antes de continuar.\n\nRecuerde que solo es posible tener una sesión de Amazon abierta a la vez.`);
                    return window.location.reload();
                }
            };
        })();
        window.onfocus = forOnFocus;
    }

    private async disconnectWs() {
        if (this._wsClient) {
            await this._wsClient.dispose();
            this._wsClient = null;
        }
    }

    logOut() {
        const cookies = getCookies();
        localStorage.removeItem(LEGACY_COOKIE);
        if (cookies[LEGACY_COOKIE] || cookies[FP_SESSION_COOKIE]) {
            const domain = '.fiscalpop.com';
            document.cookie = `${LEGACY_COOKIE}=;domain=${domain};expires=Thu, 01 Jan 1970 00:00:00 UTC;`;
            document.cookie = `${FP_SESSION_COOKIE}=;domain=${domain};path=/;expires=Thu, 01 Jan 1970 00:00:00 UTC;`;
        }
        this.disconnectWs();
        this._client.clearStore()
            .then(() => {
                fetch(LOGOUT_URL, { method: 'GET', credentials: 'include' })
                    .catch(e => {
                        console.error('Error fetching logout: ', e);
                    })
                    .then(() => {
                        window.location.href = HOME_REDIRECT_URL;
                    });
            });
    }

    submitFiscalpopClient(client: FiscalpopProfile) {
        return this._client.mutate({
            mutation: graphqlSchema.FISCALPOP.SETUP.setFiscalpopClient,
            variables: { client },
        });
    }

    get client() {
        return this._client;
    }
}


const GraphQlClient = (() => {
    const client = new GraphQlClientService();
    return client;
})();
export default GraphQlClient;
